import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VCardCreate from './VCardCreate';
import AuthContext from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VCardEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [vcardData, setVcardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVCard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/vcards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform vCard data to form format
      const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
      const profileImage = response.data.profileImage || '';
      const welcomeImage = response.data.welcomeScreenImage || '';
      
      const transformedData = {
        primaryColor: response.data.primaryColor || '#6594FF',
        secondaryColor: response.data.secondaryColor || '#FFFFFF',
        backgroundColor: response.data.backgroundColor || '#F5F5F5',
        fullName: response.data.fullName || '',
        profileImage: profileImage,
        profileImageUrl: profileImage 
          ? (profileImage.startsWith('http') || profileImage.startsWith('data:') 
              ? profileImage 
              : `${apiUrl}${profileImage}`)
          : '',
        phoneNumber: response.data.phoneNumbers?.[0] || '',
        alternativePhoneNumbers: response.data.alternativePhoneNumbers || [],
        website: response.data.website || '',
        emails: response.data.emails || [],
        companyName: response.data.companyName || '',
        title: response.data.title || '',
        summary: response.data.summary || '',
        addressType: response.data.addressType || 'manual',
        address: response.data.address || {
          street: '',
          postalCode: '',
          city: '',
          state: '',
          country: '',
        },
        addressUrl: response.data.addressUrl || '',
        socialMedia: response.data.socialMedia || [],
        welcomeScreenImage: welcomeImage,
        welcomeScreenImageUrl: welcomeImage
          ? (welcomeImage.startsWith('http') || welcomeImage.startsWith('data:')
              ? welcomeImage
              : `${apiUrl}${welcomeImage}`)
          : '',
        qrConfig: response.data.qrConfig || null,
        uniqueId: response.data.uniqueId || null,
        services: response.data.services || [],
        brochures: response.data.brochures || [],
      };
      
      setVcardData(transformedData);
    } catch (error) {
      console.error('Error fetching vCard:', error);
      alert('Failed to load vCard. You may not have permission to edit it.');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchVCard();
  }, [id, user, navigate, fetchVCard]);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading vCard...</div>;
  }

  if (!vcardData) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>vCard not found</div>;
  }

  // Custom save handler for edit mode
  const handleSave = async (formData) => {
    if (!formData.fullName) {
      alert('Full name is required');
      return;
    }

    try {
      const submitData = {
        ...formData,
        phoneNumbers: formData.phoneNumber ? [formData.phoneNumber] : [],
      };

      // Remove image preview URLs but keep image paths
      delete submitData.profileImageUrl;
      delete submitData.welcomeScreenImageUrl;

      // Optimize QR config if needed
      if (submitData.qrConfig?.image) {
        const base64Size = submitData.qrConfig.image.length * 0.75;
        if (base64Size > 2 * 1024 * 1024) {
          console.warn('QR logo image is large, consider optimizing');
        }
      }

      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/vcards/${id}`,
        submitData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating vCard:', error);
      if (error.response?.status === 413 || error.message?.includes('too large')) {
        alert('The data is too large. Please reduce the size of your QR code logo image.');
      } else {
        alert('Failed to update vCard. Please try again.');
      }
      throw error;
    }
  };

  // Pass initial data to VCardCreate component with custom save handler
  return <VCardCreate initialFormData={vcardData} isEditMode={true} vCardId={id} onSave={handleSave} />;
};

export default VCardEdit;
